import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Rating from "@/components/base/Rating";
import EmptyState from "@/components/base/EmptyState";
import { getTouristSpotBySlug, getTouristSpots } from "@/services/publicApi";
import type { TouristSpot } from "@/types";

export default function TouristSpotDetail() {
  const { slug = "" } = useParams();
  const [spot, setSpot] = useState<TouristSpot | null>(null);
  const [all, setAll] = useState<TouristSpot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getTouristSpotBySlug(slug), getTouristSpots()])
      .then(([s, list]) => {
        setSpot(s ?? null);
        setAll(list);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const nearby = useMemo(
    () => all.filter((s) => s.id !== spot?.id && s.district === spot?.district).slice(0, 3),
    [all, spot],
  );

  const mapSrc = spot ? `https://www.google.com/maps?q=${encodeURIComponent(`${spot.name}, ${spot.locationName}, ${spot.district}, Karnataka`)}&output=embed` : "";

  if (!loading && !spot) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20">
        <EmptyState
          icon="ri-landscape-line"
          title="Tourist spot not found"
          description="This place doesn't exist or the link is incorrect."
          action={{ label: "All tourist spots", to: "/tourist-spots" }}
        />
      </div>
    );
  }

  const s = spot;

  return (
    <>
      <Seo
        title={s ? `${s.name} — Timings, Entry Fee & Location` : "Tourist Spot"}
        description={s ? `${s.description} Best time to visit: ${s.bestTime}. Timings: ${s.timings}. Entry: ${s.entryFee}.` : undefined}
        keywords={s ? `${s.name}, ${s.name} timings, ${s.name} entry fee, tourist places ${s.district}, ${s.season.toLowerCase()} places in Karnataka` : undefined}
        path={`/spot/${slug}`}
        image={s?.image}
        jsonLd={
          s
            ? {
                "@context": "https://schema.org",
                "@type": "TouristAttraction",
                name: s.name,
                description: s.description,
                address: { "@type": "PostalAddress", addressLocality: s.locationName, addressRegion: "Karnataka", addressCountry: "IN" },
                touristType: s.season,
                ...(s.rating ? { aggregateRating: { "@type": "AggregateRating", ratingValue: s.rating, reviewCount: 120 } } : {}),
              }
            : undefined
        }
      />

      <div className="relative w-full">
        <div className="absolute inset-0">
          {s && <img src={s.image} alt={s.name} title={`${s.name} ${s.locationName}`} className="h-full w-full object-cover object-top" />}
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/50 to-foreground-950/85"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-20">
          <div className="[&_a]:text-background-200 [&_span]:text-background-200">
            <Breadcrumbs items={[{ label: "Tourist Spots", to: "/tourist-spots" }, { label: s?.name || "…" }]} />
          </div>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl md:text-5xl font-extrabold tracking-tight text-background-50">
            {s?.name || "Loading…"}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1.5 text-xs font-bold text-foreground-950 whitespace-nowrap">
              <i className="ri-map-pin-2-fill"></i> {s?.district}
            </span>
            {s && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1.5 text-xs font-bold text-foreground-950 whitespace-nowrap">
                <i className="ri-leaf-line"></i> Best in {s.season}
              </span>
            )}
            {s && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1.5 text-xs font-semibold text-background-50 backdrop-blur ring-1 ring-inset ring-background-50/20 whitespace-nowrap">
                <i className="ri-road-map-line"></i> {s.locationName}
              </span>
            )}
            {s && (
              <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1.5 text-xs font-semibold text-background-50 backdrop-blur ring-1 ring-inset ring-background-50/20">
                <Rating value={s.rating} />
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 lg:gap-8">
          <div className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-extrabold text-foreground-950">About this place</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">{s?.description}</p>
              <div className="mt-6 flex flex-wrap gap-4">
                <div className="flex items-center gap-3 rounded-lg bg-background-100/70 px-4 py-3">
                  <i className="ri-time-line text-xl text-secondary-600"></i>
                  <div>
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Timings</span>
                    <span className="block text-sm font-bold text-foreground-950">{s?.timings}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-background-100/70 px-4 py-3">
                  <i className="ri-ticket-2-line text-xl text-secondary-600"></i>
                  <div>
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Entry fee</span>
                    <span className="block text-sm font-bold text-foreground-950">{s?.entryFee}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-background-100/70 px-4 py-3">
                  <i className="ri-sun-line text-xl text-secondary-600"></i>
                  <div>
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Best time to visit</span>
                    <span className="block text-sm font-bold text-foreground-950">{s?.bestTime}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-background-100/70 px-4 py-3">
                  <i className="ri-leaf-line text-xl text-secondary-600"></i>
                  <div>
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Best season</span>
                    <span className="block text-sm font-bold text-foreground-950">{s?.season}</span>
                  </div>
                </div>
              </div>
            </div>

            {nearby.length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-extrabold text-foreground-950">
                  More to see in {s?.district}
                </h2>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {nearby.map((n) => (
                    <Link
                      key={n.id}
                      to={`/spot/${n.slug}`}
                      className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 cursor-pointer"
                    >
                      <div className="h-32 w-full overflow-hidden">
                        <img src={n.image} alt={n.name} title={n.name} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="p-3">
                        <span className="block font-heading text-sm font-bold text-foreground-950 leading-snug">{n.name}</span>
                        <span className="mt-1 block text-xs text-foreground-500">{n.timings}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-extrabold text-foreground-950">How to get there</h2>
              <p className="mt-2 text-sm text-foreground-600">
                {s?.name}, {s?.locationName}, {s?.district}, Karnataka — open this location in Google Maps for directions.
              </p>
              <div className="mt-4 overflow-hidden rounded-lg border border-background-200">
                {mapSrc && (
                  <iframe title={`Map to ${s?.name}`} src={mapSrc} className="h-72 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
                )}
              </div>
            </section>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 h-fit">
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">Plan your visit</h2>
              <p className="mt-2 text-sm text-foreground-700">
                Looking for cabs, homestays or guides nearby? Find trusted local businesses in {s?.locationName}.
              </p>
              <Link
                to={`/directory?q=${encodeURIComponent(s?.locationName || "")}`}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line"></i> Find travel services
              </Link>
              <Link
                to="/tourist-spots"
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-map-2-line"></i> All tourist spots
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}